import React from 'react';

import {ClickToEditButton} from 'app/components/general/ClickToEditButton';
import {StartDocumentButton} from 'app/components/general/StartDocumentPopover';
import {DocumentFoldersList} from 'app/folders/components/DocumentFoldersList';
import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import {DocumentMetadataStatusButtons} from 'app/general/structures/DocumentMetadataStatusButtons';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';
import {ImagesCarousel} from 'app/general/widgets/ImagesCarousel';
import {DocumentPolicyList} from 'app/policies/components/DocumentPolicyList';
import {DocumentTagsList} from 'app/tags/components/DocumentTagsList';
import {getProcedureName} from 'app/utils/fallbacks';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import {ManagersList} from './ManagersList';
import {ModifiedCreatedDataStamp} from './ModifiedCreatedDataStamp';

import type {ProcedureContentWithDiffType} from 'app/tasks/types';
import type {ProcedureHashidSerializedType} from 'sweet-types';

type Props = {
  content: Pick<ProcedureContentWithDiffType, 'name' | 'description'>;
  procedure: ProcedureHashidSerializedType;
  warningComponent?: JSX.Element;
  containerAdditionalClass?: string;
  advancedModeEnabled?: boolean;
  editableEmptyDataMessage?: string;
  procedureAndUserInSameAccount?: boolean;
  mediaURL: string;
  mediaURLRead: string;
  editMode?: boolean;
};

export class ViewProcedureMetaData extends React.PureComponent<Props> {
  static defaultProps = {
    containerAdditionalClass: '',
  };

  stopImagesClickPropagation(e: any) {
    // allow users
    e.stopPropagation();
  }

  render() {
    const {
      content,
      procedure,
      mediaURL,
      mediaURLRead,
      editableEmptyDataMessage,
      advancedModeEnabled,
      procedureAndUserInSameAccount,
      warningComponent,
      editMode,
    } = this.props;

    const hasNoTagsOrDescription =
      procedure.tags.length === 0 && content.description.content === '';

    const embeds = [...(content.description.embeds ?? [])];
    const carousel = [...content.description.images, ...embeds];
    return (
      <DocHeaderStructure
        warningContent={warningComponent}
        advancedContent={
          <div className="row">
            {advancedModeEnabled === true ? (
              <div className="col">
                <ModifiedCreatedDataStamp document={procedure} />
                {procedure.managers != null ? (
                  <ManagersList document={procedure} />
                ) : null}
              </div>
            ) : null}
            <div className="col-auto ml-auto text-muted d-flex align-items-center mb-1">
              <DocumentMetadataStatusButtons
                canEdit={procedure.can_edit}
                contentType="procedure"
                context={'view'}
                document={procedure}
              />
            </div>
          </div>
        }
        titleContent={
          <div
            className={
              isRightToLeftWrapper(content.name) ? 'right-to-left-text' : ''
            }
          >
            <h1 role="heading" aria-level={1}>
              {typeof content.name === 'string'
                ? getProcedureName(content, true)
                : content.name}
              {editMode ? <ClickToEditButton /> : null}
            </h1>
            {procedureAndUserInSameAccount === true ? (
              <span
                className={`d-print-none ${
                  isRightToLeftWrapper(content.name)
                    ? 'float-left'
                    : 'float-right'
                } mr-1 ml-1`}
              >
                <StartDocumentButton
                  objectId={procedure.id}
                  contentType="procedure"
                />
              </span>
            ) : null}
          </div>
        }
        tagsContent={
          <>
            {hasNoTagsOrDescription && editableEmptyDataMessage != null ? (
              <div className="form-group">
                <span className="small text-secondary font-weight-light">
                  {editableEmptyDataMessage}
                </span>
              </div>
            ) : (
              <DocumentTagsList tags={procedure.tags} />
            )}
            <DocumentFoldersList connections={procedure.connections} />
          </>
        }
        policyContent={<DocumentPolicyList policies={procedure.policies} />}
        descriptionContent={
          <div>
            <UserContentContainer
              id="doc-description"
              mediaURL={mediaURL}
              mediaURLRead={mediaURLRead}
              content={content.description.content}
            />

            {carousel.length > 0 ? (
              <div onClick={this.stopImagesClickPropagation}>
                <ImagesCarousel images={carousel} className="step-images" />
              </div>
            ) : null}
          </div>
        }
      />
    );
  }
}
